'use client'
import * as React from 'react'
import {
  Alert,
  Stack,
  useTheme,
  TextField,
  Typography,
  useMediaQuery,
  Box, Checkbox,
  InputAdornment,
  FormControlLabel,
  alpha,
  useColorScheme,
} from "@mui/material"
import Image from "next/image"
import { appLinks } from "@/lib/links"
import { GLOBAL_PATHS } from "@/lib/globals"
import { AuthProvider } from "@toolpad/core"
import { useSession } from "next-auth/react"
import { signInUser } from "@/app/login/actions"
import { useSearchParams, useRouter } from "next/navigation"
import { providerMap } from "@/services/security/auth/providers"
import { AuthResponse, SignInPage } from "@toolpad/core/SignInPage"

import Turnstile from "react-turnstile"
import { TranslationContext } from "@/configs/context"
import LoadingWrapper from "@/ui/components/common/Loading"

import AlternateEmailIcon from '@mui/icons-material/AlternateEmail'
import { isEmail } from '@/lib/libs'

enum Error {
  Error = "Error",
  Configuration = "Configuration",
  Verification = "Verification",
  OAuthAccountNotLinked = "OAuthAccountNotLinked",
}

export default function LoginWrapper() {
  const theme = useTheme()
  const router = useRouter()
  const search = useSearchParams()
  const { colorScheme } = useColorScheme()
  const { data: session, status } = useSession()
  // @ts-expect-error:next-line
  const { t, lang }: object = React.useContext(TranslationContext)
  const matchMd = useMediaQuery(theme.breakpoints.up('md'))

  const isDarkMode = colorScheme === "dark"
  const errorReq = search.get("error") as Error

  const [error, setError] = React.useState()
  const [email, setEmail] = React.useState("")
  const [emailError, setEmailError] = React.useState(false)
  const [turnstileKey, setTurnstileKey] = React.useState(0)
  const [turnstileToken, setTurnstileToken] = React.useState<string | null>(null)

  const emailRef = React.useRef<any>(null)
  // Utiliser useRef pour la référence Turnstile
  const turnstileRef = React.useRef<any>(null)

  // En développement, considérer Turnstile comme validé
  const isTurnstileValid = React.useMemo(() => {
    return process.env.NODE_ENV === "development" || !!turnstileToken
  }, [turnstileToken])

  const errorMap = React.useMemo(() => ({
    [Error.Error]: (t('translation:error.login_error_config')),
    [Error.Configuration]: (t('translation:error.login_error_config')),
    [Error.Verification]: (t('translation:error.login_error_config')),
    [Error.OAuthAccountNotLinked]: (t('translation:error.login_error_account_exists')),
  }), [t])

  // Gestionnaires Turnstile avec useCallback pour éviter les re-renders
  const handleTurnstileSuccess = React.useCallback((token: string) => {
    setTurnstileToken(token)
    setError(undefined)
  }, [])

  const handleTurnstileError = React.useCallback(() => {
    setTurnstileToken(null)
    setError(t('translation:error.captcha_error'))
  }, [t])

  const handleTurnstileExpire = React.useCallback(() => {
    setTurnstileToken(null)
    setError(t('translation:error.captcha_expired'))
  }, [t])

  // Gestion de la touche Entrée pour soumettre le formulaire de connexion
  const handleKeyDown = React.useCallback((event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && isTurnstileValid) {
      event.preventDefault()
      // Déclencher la soumission du formulaire
      const form = event.currentTarget.closest('form')
      if (form) {
        const submitButton = form.querySelector('button[type="submit"]') as HTMLButtonElement
        if (submitButton && !submitButton.disabled) {
          submitButton.click()
        }
      }
    }
  }, [isTurnstileValid])

  // Mémoriser le composant Turnstile pour éviter les re-renders inutiles
  const TurnstileComponent = React.useMemo(() => {
    if (process.env.NODE_ENV !== "production") return null

    return (
      <Stack
        className='turnstile-box'
        direction="row"
        justifyContent="center"
      >
        <Turnstile
          theme="auto"
          language={lang}
          size="flexible"
          // key={turnstileKey}
          // ref={turnstileRef}
          // appearance="execute"
          onError={handleTurnstileError}
          onExpire={handleTurnstileExpire}
          onVerify={handleTurnstileSuccess}
          className="clfr-turnstile-wrapper"
          sitekey={process.env.NEXT_PUBLIC_CLOUDFLARE_TURNSITE_SITE_KEY as string}
        />
      </Stack>
    )
  }, [handleTurnstileError, handleTurnstileExpire, handleTurnstileSuccess, lang])

  // Mémoriser le composant CustomEmailField pour éviter les re-renders
  const CustomEmailField = React.useMemo(() => {
    return (
      <Box>
        <TextField
          required
          fullWidth
          name="email"
          type="email"
          size="small"
          variant="outlined"
          error={emailError}
          disabled={!isTurnstileValid}
          id="input-with-icon-textfield"
          label={t('translation:login.email')}
          onKeyDown={handleKeyDown}
          sx={{ mt: 1 }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <AlternateEmailIcon fontSize="inherit" />
                </InputAdornment>
              ),
            },
          }}
        />
        {!isTurnstileValid && TurnstileComponent}
        <Box sx={{ p: 1 }}>
          <FormControlLabel
            control={
              <Checkbox
                name="tandc"
                value="true"
                color="primary"
                disabled={!isTurnstileValid}
                sx={{ padding: 0.5, '& .MuiSvgIcon-root': { fontSize: 20 } }}
              />
            }
            color="textSecondary"
            slotProps={{ typography: { fontSize: 12 } }}
            label={t('translation:login.sign_in_remember_me')}
          />
        </Box>
      </Box>
    )
  }, [emailError, isTurnstileValid, t, TurnstileComponent, handleKeyDown])

  // Mémoriser le composant AgreeWithTerms
  const AgreeWithTerms = React.useMemo(() => {
    return (
      <FormControlLabel
        color="text.primary"
        label={t('translation:login.sign_in_remember_me')}
        slotProps={{
          typography: { fontSize: 12 }
        }}
        control={
          <Checkbox
            name="tandc"
            value="true"
            color="default"
            disabled={!isTurnstileValid}
            sx={{
              padding: 0.5,
              color: `${theme.palette.background.paper} !important`,
              '& .MuiSvgIcon-root': { fontSize: 20 }
            }}
          />
        }
      />
    )
  }, [isTurnstileValid, t, theme.palette.background.paper])

  React.useEffect(() => {
    if (session && status == "authenticated") {
      router.push(
        process.env.NEXT_PUBLIC_ROUTE_ACCOUNT ?? appLinks.account.PROFILE
      )
    }
  }, [router, session, status])

  React.useEffect(() => {
    if (errorReq && !error) { setError(errorMap[errorReq]) }
  }, [errorReq, error, errorMap])

  const handleSingIn: (provider: AuthProvider, formData: FormData, callbackUrl?: string) =>
    Promise<AuthResponse> = React.useCallback(async (provider, formData) => {
      // Vérifier si Turnstile est validé avant de continuer
      if (!isTurnstileValid) {
        const errorMsg = t('translation:error.captcha_required')
        setError(errorMsg)
        return { error: errorMsg }
      }

      if (provider.id === 'nodemailer' && !isEmail(formData?.get("email") as string)) {
        return { error: t('translation:error.contact_email_error') }
      }

      const promise = new Promise<AuthResponse>(async (resolve, reject) => {
        try {
          setError(undefined)
          const result: any = await signInUser(provider, formData)

          if (result?.Error) {
            return resolve({ error: result.error })
          }
          if (result?.success) {
            return resolve({ success: result.success })
          }
        } catch (e) {
          console.log(e)
          const errorMsg = e.toString()
          setError(errorMsg)
          reject({ error: errorMsg })
        }
      })

      return promise
    }, [isTurnstileValid, t])

  // Mémoriser les props de style pour éviter les re-renders
  const signInPageSx = React.useMemo(() => ({
    "& .MuiStack-root": {
      mt: 1.5,
      borderRadius: 8,
      boxShadow: "none",
      color: "text.primary",
      border: "1px solid rgba(133, 119, 126, 0.05)",
      backdropFilter: "blur(10px) saturate(181%) !important",
      background: `${alpha(theme.palette.background.paper, isDarkMode ? 0.5 : 0.075)} !important`,
    },
    "& .turnstile-box.MuiStack-root": {
      border: "none !important",
      backdropFilter: "none !important",
      background: `transparent !important`,
    },
    ...(!isTurnstileValid ? {
      "& .MuiButtonBase-root": {
        opacity: 0.5,
        pointerEvents: "none",
        cursor: "not-allowed !important",
      },
      "& .MuiButtonBase-root:not([type='submit']):hover": {
        background: `${alpha(theme.palette.common[isDarkMode ? "black" : "white"], 0.5)} !important`,
      }
    } : {
      // "& .MuiButton-glass": {
      //   color: "#ffffff",
      //   borderRadius: "50px",
      //   border: "1px solid rgba(133, 119, 126, 0.1)",
      // }
    })
  }), [isDarkMode, isTurnstileValid, theme.palette.background.paper, theme.palette.common])

  const submitButtonProps = React.useMemo(() => ({
    size: "large" as const,
    variant: "black-glass" as const,
    disabled: !isTurnstileValid,
    sx: {
      mt: 2,
      height: 42,
      opacity: isTurnstileValid ? 1 : 0.6,
      cursor: isTurnstileValid ? "pointer" : "not-allowed",
      background: `${alpha(theme.palette.common.black, isTurnstileValid ? 1 : 0.3)} !important`,
    }
  }), [isTurnstileValid, theme.palette.common.black])

  const renderDevUI = () => {
    return (
      <Box sx={{ height: "100vh" }}>
        <Stack
          direction="column"
          alignItems="center"
          justifyContent="center"
          sx={{ py: 4, height: "100%" }}
        >
          <Image
            alt=""
            width={250}
            height={250}
            src={`${GLOBAL_PATHS.IMAGES}4537995.webp`}
          />
          <Typography variant="h4" align="center" sx={{ fontWeight: "bold" }}>
            {t('translation:common.coming_soon')}
          </Typography>
          <Typography
            align="center"
            sx={{ marginTop: "0.5rem" }}
            variant={matchMd ? "h6" : "body1"}
          >
            {t('translation:common.coming_soon_description')}
          </Typography>
        </Stack>
      </Box>
    )
  }

  const renderLoadingUI = () => {
    return (
      <React.Suspense fallback={<div />}>
        <Box sx={{ height: "100vh" }}>
          <Stack
            direction="column"
            alignItems="center"
            justifyContent="center"
            sx={{ py: 4, height: "100%" }}
          >
            <LoadingWrapper error={undefined} />
          </Stack>
        </Box>
      </React.Suspense>
    )
  }

  if (!status || status !== 'unauthenticated') {
    return renderLoadingUI()
  }

  return (
    <React.Suspense fallback={<div />}>
      <Box>
        <SignInPage
          sx={signInPageSx}
          signIn={handleSingIn}
          providers={providerMap}
          slotProps={{
            submitButton: submitButtonProps,
            emailField: { autoFocus: false },
            form: { noValidate: true }
          }}
          slots={{ rememberMe: () => AgreeWithTerms, emailField: () => CustomEmailField }}
          localeText={{
            to: t('translation:login.to'),
            or: t('translation:login.or'),
            with: t('translation:login.with'),
            email: t('translation:login.email'),
            passkey: t('translation:login.passkey'),
            password: t('translation:login.password'),
            signInTitle: t('translation:login.sign_in_title'),
            signInSubtitle: t('translation:login.sign_in_subtitle'),
            signInRememberMe: t('translation:login.sign_in_remember_me'),
            providerSignInTitle: (provider: string) => t('translation:login.provider_sign_in_title', { provider }),
          }}
        />

        <Stack direction="row" justifyContent="center">
          {error &&
            <Alert
              severity="error"
              sx={{ mx: 2, mb: 1, bottom: 16, alignSelf: "center", position: "absolute" }}
            >
              {error}
            </Alert>}
        </Stack>
      </Box>
    </React.Suspense>
  )
}